'use client';

import {
  createElement,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from 'react';

type InstantClockDisplayedTextProps<T extends ElementType = 'span'> = {
  /** Host element. Defaults to `span`. */
  as?: T;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children'>;

/**
 * Leaf wrapper for user-visible copy that includes a clock fragment (hour/minute),
 * including i18n strings built with `toLocaleTimeString` or keys such as
 * `tx_details_date_hour`.
 *
 * Applies `suppressHydrationWarning` on the rendered element so SSR (often UTC) and the
 * browser (user timezone) may differ without hydration warnings.
 *
 * @see docs/adr/may-11-2026-timescalar-client-component-boundary.md — solution item 5
 * (`TimeScalar` client component boundary).
 *
 * Do not use for date-only calendar strings unless the visible output includes clock
 * time.
 *
 * @param props.as - Host element type; defaults to **`span`** when omitted.
 */
export const InstantClockDisplayedText = <T extends ElementType = 'span'>({
  as,
  children,
  ...rest
}: InstantClockDisplayedTextProps<T>) => {
  const Component = (as ?? 'span') as ElementType;

  return createElement(
    Component,
    { ...rest, suppressHydrationWarning: true },
    children,
  );
};
